(function () {
  'use strict';

  /* =========================================================
     Divine Hindu UX layer
     1. Floating icons: Divine Coins chip pinned bottom-LEFT, chatbot bubble pinned
        bottom-RIGHT — same size (48px), same bottom line, exactly opposite each other.
        (Dragging removed: it caused the two icons to fall out of alignment.)
     2. Chat panel: compact, opens above the bubble, website stays visible
     3. While the chat panel is OPEN the page behind CANNOT scroll — even on iOS
        with the keyboard open (body position lock + touchmove guard).
     4. Keyboard: lifts only the chat panel above the keyboard — page never moves/zooms
     5. "Talk to an Acharya" (wa.me links) opens the chatbot instead of WhatsApp
     ========================================================= */

  var BUBBLE = 48; /* bubble size in px — keep in sync with #dhb-bubble CSS in theme.liquid */
  var bubble = null;

  /* Old versions let the bubble be dragged and saved its position — that is what
     pushed the two icons out of line. Remove any saved position for good. */
  try { localStorage.removeItem('dhb-pos-v2'); localStorage.removeItem('dhb-pos'); } catch (e) {}

  function vw() { return window.innerWidth; }
  function vh() { return window.innerHeight; }
  function clamp(v, min, max) { return Math.max(min, Math.min(max, v)); }
  function getPanel() { return document.getElementById('dhb-panel'); }

  /* PERF (mid-range Android): pinBubble() used to rewrite five !important inline
     properties every single time it was called — and the safety-net heartbeat below
     called it 2.5 times a second for the lifetime of every page. Each write
     invalidates style for the element even when the value is identical, so the
     browser was doing style work ~150 times a minute to change nothing. It is now a
     no-op once the bubble is pinned; anything that could actually knock it out of
     place (resize, a click on the bubble, the widget mutating the DOM) calls
     unpinBubble() first so the next pinBubble() really writes. */
  var pinned = false;
  function unpinBubble() { pinned = false; }

  function pinBubble() {
    if (!bubble || pinned) return;
    pinned = true;
    /* Hard-pin: fixed, bottom-right, mirroring the Divine Coins chip on the left.
       Clear any inline left/top a previous version (or the widget itself) set. */
    bubble.style.setProperty('position', 'fixed', 'important');
    bubble.style.setProperty('right', '14px', 'important');
    bubble.style.setProperty('bottom', 'var(--dh-fab-bottom, 16px)', 'important');
    bubble.style.setProperty('left', 'auto', 'important');
    bubble.style.setProperty('top', 'auto', 'important');
  }

  function positionPanel() {
    var panel = getPanel();
    if (!panel || !bubble) return;
    var r = bubble.getBoundingClientRect();
    var pw = Math.min(340, vw() - 20);
    var left = (r.left + r.width / 2 <= vw() / 2) ? r.left : (r.right - pw);
    left = clamp(left, 10, vw() - pw - 10);
    panel.style.setProperty('position', 'fixed', 'important');
    panel.style.setProperty('width', pw + 'px', 'important');
    panel.style.setProperty('left', left + 'px', 'important');
    panel.style.setProperty('right', 'auto', 'important');
    if (r.top > vh() / 2) {
      panel.style.setProperty('bottom', (vh() - r.top + 10) + 'px', 'important');
      panel.style.setProperty('top', 'auto', 'important');
    } else {
      panel.style.setProperty('top', (r.bottom + 10) + 'px', 'important');
      panel.style.setProperty('bottom', 'auto', 'important');
    }
  }

  /* ---------- Background scroll lock while the chat panel is open ----------
     overflow:hidden alone is NOT enough on iOS Safari (especially with the
     keyboard open), so we combine three layers:
       a) html.dhb-lock { overflow:hidden }               (desktop browsers)
       b) body position:fixed freeze with scroll restore  (iOS)
       c) a non-passive touchmove guard that blocks scroll gestures that start
          outside the chat panel                          (iOS + keyboard)      */
  var locked = false;
  var savedScrollY = 0;

  function panelOpen() {
    var p = getPanel();
    if (!p) return false;
    if (p.offsetWidth <= 0 || p.offsetHeight <= 0) return false;
    var cs = getComputedStyle(p);
    return cs.display !== 'none' && cs.visibility !== 'hidden' && parseFloat(cs.opacity || '1') > 0.05;
  }

  function lock() {
    if (locked) return;
    locked = true;
    savedScrollY = window.pageYOffset || document.documentElement.scrollTop || 0;
    document.documentElement.classList.add('dhb-lock');
    var bs = document.body.style;
    bs.setProperty('position', 'fixed', 'important');
    bs.setProperty('top', (-savedScrollY) + 'px', 'important');
    bs.setProperty('left', '0', 'important');
    bs.setProperty('right', '0', 'important');
    bs.setProperty('width', '100%', 'important');
    bs.setProperty('overflow', 'hidden', 'important');
  }

  function unlock() {
    if (!locked) return;
    locked = false;
    document.documentElement.classList.remove('dhb-lock');
    var bs = document.body.style;
    bs.removeProperty('position');
    bs.removeProperty('top');
    bs.removeProperty('left');
    bs.removeProperty('right');
    bs.removeProperty('width');
    bs.removeProperty('overflow');
    /* Restore instantly — html{scroll-behavior:smooth} would otherwise animate
       from the top through the whole page, which looks like a giant scroll jump. */
    var de = document.documentElement, prevSB = de.style.scrollBehavior;
    de.style.scrollBehavior = 'auto';
    try { window.scrollTo({ top: savedScrollY, left: 0, behavior: 'instant' }); }
    catch (err) { window.scrollTo(0, savedScrollY); }
    de.style.scrollBehavior = prevSB;
  }

  /* Lock only on mobile — on desktop the site stays scrollable behind the chat. */
  var MOBILE_MQ = window.matchMedia('(max-width: 989px)');
  function syncLock() {
    if (panelOpen() && MOBILE_MQ.matches) lock(); else unlock();
  }

  /* Block scroll gestures outside the panel while locked (must be non-passive). */
  document.addEventListener('touchmove', function (e) {
    if (!locked) return;
    var p = getPanel();
    if (p && p.contains(e.target)) return; /* let the chat itself scroll */
    if (e.cancelable) e.preventDefault();
  }, { passive: false });

  function watchPanel() {
    var p = getPanel();
    if (!p || p.__dhLockWatched) return;
    p.__dhLockWatched = true;
    new MutationObserver(function () { syncLock(); positionPanel(); })
      .observe(p, { attributes: true, attributeFilter: ['style', 'class', 'hidden'] });
    syncLock();
  }
  /* Safety net: heartbeat in case the widget toggles the panel in a way the observers
     miss.
     PERF: this was a flat 400ms interval forever. syncLock() -> panelOpen() reads
     offsetWidth/offsetHeight and getComputedStyle(), which is a forced synchronous
     layout + style recalc; once the chat widget has rendered #dhb-panel that was
     happening 2.5 times a second on every page for as long as the tab stayed open,
     for a panel the user opens maybe once a session.
     It now runs "hot" (400ms, unchanged responsiveness) for 10s after anything that
     could plausibly have toggled the panel — a click, a resize, a DOM mutation near
     the widget — and idles at 2.5s otherwise. It also stops entirely while the tab is
     hidden, since nothing can be toggled by a user who is not looking at the page. */
  var HOT_MS = 10000, hotUntil = 0, beat = null, beatPeriod = 0;
  function goHot() {
    hotUntil = Date.now() + HOT_MS;
    if (beatPeriod !== 400) schedule(400);
  }
  function schedule(period) {
    if (beat) clearInterval(beat);
    beatPeriod = period;
    beat = setInterval(function () {
      if (document.hidden) return;
      syncLock();
      if (bubble) pinBubble();
      var wantHot = Date.now() < hotUntil || locked;
      if (wantHot && beatPeriod !== 400) schedule(400);
      else if (!wantHot && beatPeriod !== 2500) schedule(2500);
    }, period);
  }
  schedule(400);
  document.addEventListener('click', goHot, true);
  document.addEventListener('visibilitychange', function () { if (!document.hidden) goHot(); });
  window.addEventListener('resize', function () { unpinBubble(); goHot(); }, { passive: true });

  function initBubble() {
    bubble = document.getElementById('dhb-bubble');
    if (!bubble) return false;
    pinBubble();
    positionPanel();
    bubble.addEventListener('click', function () {
      goHot();
      setTimeout(function () { unpinBubble(); pinBubble(); positionPanel(); watchPanel(); syncLock(); }, 60);
      setTimeout(function () { unpinBubble(); pinBubble(); positionPanel(); watchPanel(); syncLock(); }, 400);
    });
    /* The widget can re-append or restyle the bubble, so drop the pin cache whenever
       body's direct children change and let the next heartbeat re-pin it. */
    new MutationObserver(function () { unpinBubble(); positionPanel(); watchPanel(); syncLock(); goHot(); }).observe(document.body, { childList: true, subtree: false });
    window.addEventListener('resize', function () { unpinBubble(); pinBubble(); positionPanel(); });
    watchPanel();
    /* "Chat with us" tag: peek for 3s every 30s (CSS ::after/::before toggled via .dh-say).
       Skipped while the cart drawer is open (the bubble is hidden then anyway). */
    setInterval(function () {
      if (!bubble || document.documentElement.classList.contains('cart-open')) return;
      bubble.classList.add('dh-say');
      setTimeout(function () { if (bubble) bubble.classList.remove('dh-say'); }, 3000);
    }, 30000);
    return true;
  }
  var tries = 0;
  var poll = setInterval(function () { if (initBubble() || ++tries > 80) clearInterval(poll); }, 250);

  /* ---------- Loyalty-widget account menu: restore the real Order History ----------
     The Divine Coins widget adds its own emoji menu (📦 Order History / 🚪 Logout) and a
     full-screen "Order History" overlay of its own. Customers expect Shopify's real
     order history (/account) — so we intercept that menu item and route it natively,
     and we clean the emoji styling so the menu matches the theme. */
  var EMOJI_BOX = '\uD83D\uDCE6';   /* 📦 */
  var EMOJI_DOOR = '\uD83D\uDEAA';  /* 🚪 */

  function goOrderHistory() {
    try {
      var cid = (typeof getCustomerId === 'function') ? getCustomerId() : null;
      if (!cid && typeof handleKpAndShopifyLogin === 'function') { handleKpAndShopifyLogin('/account'); return; }
    } catch (err) {}
    location.href = '/account';
  }

  document.addEventListener('click', function (e) {
    /* after any click, give the widget a moment to render its menu, then theme it */
    setTimeout(themeDpMenu, 150);
    setTimeout(themeDpMenu, 450);
    setTimeout(themeDpMenu, 900);
    var t = e.target;
    if (!t || !t.closest) return;
    if (t.closest('#kwikpass-drawer-desktop') || t.closest('#dhb-panel')) return;
    var hit = t.closest('a,button,[role="button"],p,li,div,span');
    if (!hit) return;
    var txt = (hit.textContent || '').replace(/\s+/g, ' ').trim();
    if (txt === 'Order History' || txt === EMOJI_BOX + ' Order History' || txt === EMOJI_BOX + 'Order History') {
      e.preventDefault();
      e.stopImmediatePropagation();
      goOrderHistory();
      return;
    }
  }, true);

  /* the widget may render its (hidden) menu at load for logged-in users — clean it early too */
  [1500, 3000, 5000, 8000].forEach(function (d) { setTimeout(themeDpMenu, d); });

  function themeDpMenu() {
    var walker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT, null, false);
    var node, hits = [];
    while ((node = walker.nextNode())) {
      var v = node.nodeValue || '';
      if (v.indexOf(EMOJI_BOX) > -1 || v.indexOf(EMOJI_DOOR) > -1) {
        if (node.parentElement && node.parentElement.closest && node.parentElement.closest('#dhb-panel')) continue;
        hits.push(node);
      }
    }
    hits.forEach(function (n) {
      var txt = (n.nodeValue || '');
      if (!/Order History|Logout/i.test(txt)) return; /* only the account menu, nothing else */
      n.nodeValue = txt.replace(EMOJI_BOX, '').replace(EMOJI_DOOR, '').replace(/^\s+/, '');
      var el = n.parentElement;
      if (el) {
        el.style.setProperty('font-family', 'var(--font-body)', 'important');
        el.style.setProperty('font-size', '14px', 'important');
        el.style.setProperty('font-weight', '600', 'important');
        el.style.setProperty('color', '#1a1a1a', 'important');
      }
      /* climb to the floating card and align it with the theme */
      var box = el, i = 0;
      while (box && i < 5) {
        var cs = getComputedStyle(box);
        if (cs.position === 'absolute' || cs.position === 'fixed') {
          box.style.setProperty('border-radius', '12px', 'important');
          box.style.setProperty('border', '1px solid #eee2cf', 'important');
          box.style.setProperty('box-shadow', '0 10px 30px rgba(0,0,0,.12)', 'important');
          box.style.setProperty('background', '#fff', 'important');
          box.style.setProperty('font-family', 'var(--font-body)', 'important');
          break;
        }
        box = box.parentElement; i++;
      }
    });
  }

  /* ---------- Talk to Acharya -> chatbot ----------
     Only the Acharya number opens the on-site chatbot. Every other wa.me link
     (e.g. the bottom-nav WhatsApp button -> 8130978800) opens real WhatsApp. */
  document.addEventListener('click', function (e) {
    var a = e.target.closest && e.target.closest('a[href*="wa.me"]');
    if (!a) return;
    if ((a.getAttribute('href') || '').indexOf('9220239256') === -1) return;
    var b = document.getElementById('dhb-bubble');
    if (!b) return; /* chatbot not loaded -> keep WhatsApp behaviour */
    e.preventDefault();
    e.stopImmediatePropagation();
    if (!panelOpen()) b.click();
  }, true);

  /* ---------- Keyboard: lift only the chat panel, never the page ----------
     iOS reports the keyboard through visualViewport. Android Chrome does NOT here,
     because the page uses interactive-widget=overlays-content — so on Android we
     read the keyboard size from the VirtualKeyboard API instead. */
  var kbOverlayHeight = 0;

  function liftPanel() {
    var p = getPanel();
    if (!p) return;
    var ae = document.activeElement;
    var inChat = ae && p.contains(ae);
    var vv = window.visualViewport;
    var hidden = 0, visibleBottom = window.innerHeight;
    if (kbOverlayHeight > 0) {                    /* Android (VirtualKeyboard API) */
      hidden = kbOverlayHeight;
      visibleBottom = window.innerHeight - kbOverlayHeight;
    } else if (vv) {                              /* iOS Safari and others */
      hidden = window.innerHeight - vv.height - vv.offsetTop;
      visibleBottom = vv.height + vv.offsetTop;
    }
    if (inChat && hidden > 40) {
      var cur = 0;
      var m = /translateY\(-([\d.]+)px\)/.exec(p.style.transform || '');
      if (m) cur = parseFloat(m[1]) || 0;
      var r = p.getBoundingClientRect();
      var overlap = r.bottom - visibleBottom + 8;
      if (overlap > 0) p.style.setProperty('transform', 'translateY(-' + Math.round(cur + overlap) + 'px)', 'important');
    } else {
      p.style.removeProperty('transform');
    }
  }

  if ('virtualKeyboard' in navigator) {
    try {
      navigator.virtualKeyboard.overlaysContent = true;
      navigator.virtualKeyboard.addEventListener('geometrychange', function () {
        var b = navigator.virtualKeyboard.boundingRect;
        kbOverlayHeight = (b && b.height) ? b.height : 0;
        liftPanel();
      });
    } catch (e) {}
  }
  if (window.visualViewport) {
    window.visualViewport.addEventListener('resize', liftPanel);
    window.visualViewport.addEventListener('scroll', liftPanel);
  }
  document.addEventListener('focusin', function () { setTimeout(liftPanel, 120); setTimeout(liftPanel, 400); });
  document.addEventListener('focusout', function () { setTimeout(liftPanel, 120); });
})();
